import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GetMeResponse, UpdatePinRequest } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';
import { addOrUpdateMeCache } from '../queries';

type UseUpdatePinActionData = {
    updatePin: (data: UpdatePinRequest) => Promise<GetMeResponse>;
    loading: boolean;
    error: string;
};

/** Changing the signed-in user's own PIN. */
export function useUpdatePinAction(): UseUpdatePinActionData {
    const { t } = useMeTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `PATCH /v3/me/pin`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'INCORRECT_PASSWORD')) {
                return t('actions.update_pin.error_wrong_password');
            }
            if (isApiError(error, 'USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: UpdatePinRequest) =>
            openApiRef.current.me
                .updatePin(data)
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateMeCache(queryClient, data.data);

            UIToast.success(t('actions.update_pin.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        updatePin: (data) => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
